import React, { useMemo, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import capitalize from 'react-bootstrap/lib/utils/capitalize';
import { RATINGS } from '../../../lib/schema.js';
import QuestionBlueIcon from '../../icons/QuestionBlueIcon.jsx';
import { StakeholderIcon } from '../../icons/StakeholderIcon.jsx';
import RatingLabel from '../../RatingLabel.jsx';
import ProfileEngagementFrequencyModal from './ProfileEngagementFrequencyModal.jsx';
import ProfileSupportForChangeModal from './ProfileSupportForChangeModal.jsx';
import ParticipationsTree from '../../ParticipationsTree.jsx';
import OverviewTextField from '../../OverviewTextField.jsx';
import styles from './StakeholderProfile.module.css';

const StakeholderProfile = (props) => {
  const { project, employees, stakeholder, groupParticipations } = props;

  const { stakeholderFields } = project;

  const [showSupportForChangeModal, setShowSupportForChangeModal] =
    useState(false);
  const [showEngagementFrequencyModal, setShowEngagementFrequencyModal] =
    useState(false);

  const onShowSupportForChangeModal = () => setShowSupportForChangeModal(true);
  const onShowEngagementFrequencyModal = () =>
    setShowEngagementFrequencyModal(true);

  const onHideSupportForChangeModal = () => setShowSupportForChangeModal(false);
  const onHideEngagementFrequencyModal = () =>
    setShowEngagementFrequencyModal(false);

  const fieldInfluence = stakeholderFields.find(
    (field) => field.nativeColumn === 'influence'
  );
  const fieldInterest = stakeholderFields.find(
    (field) => field.nativeColumn === 'interest'
  );
  const fieldImpact = stakeholderFields.find(
    (field) => field.nativeColumn === 'impact'
  );

  const influenceRating = stakeholder[fieldInfluence.key];
  const interestRating = stakeholder[fieldInterest.key];
  const impactRating = stakeholder[fieldImpact.key];

  const sortedFields = stakeholderFields.sort(
    (a, b) => a.priority - b.priority
  );

  const stakeholderInformationNames = sortedFields.filter(
    (field) =>
      ![
        'member_count',
        'influence',
        'interest',
        'impact',
        'support',
        'engagement_frequency',
      ].includes(field.nativeColumn)
  );

  const getStakeholderField = (key) => {
    return stakeholderFields.find((value) => value.nativeColumn === key);
  };

  const showDefaultFieldNames = (name) => {
    switch (name) {
      case 'influence':
        return 'Level of influence';
      case 'interest':
        return 'Level of interest';
      case 'impact':
        return 'Predicted impact';
      default:
        return name;
    }
  };

  const renderAnalysisColors = (key, name, reverse) => {
    if (RATINGS.HIGH_MEDIUM_LOW.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel className="m-l-6" rating={key} reverse={reverse} />
        </span>
      );
    }
  };

  const renderField = (name, rating, reverse) => {
    return (
      <Row className="m-b-12">
        <Col xs={6} className="text-muted modal-key-label">
          {showDefaultFieldNames(name)}
        </Col>
        <Col xs={6}>
          {rating ? renderAnalysisColors(rating, name, reverse) : '—'}
        </Col>
      </Row>
    );
  };

  const renderSupportColors = (key) => {
    if (RATINGS.SUPPORT_LEVELS.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel className="m-l-12" rating={key} />
        </span>
      );
    }
  };

  const renderEngageColors = (key) => {
    if (RATINGS.FREQUENCY_LEVELS.includes(key)) {
      return (
        <span key={key}>
          <RatingLabel className="m-l-12" rating={key} />
        </span>
      );
    }
  };

  const renderFields = useMemo(() => {
    return stakeholderInformationNames.map((field, index) => {
      return (
        <OverviewTextField
          isFieldWithEditor={stakeholderFields.some(
            (item) => item.editor && item.key === field.key
          )}
          entity={stakeholder}
          field={field}
          index={index}
        />
      );
    });
  }, [project, stakeholder, stakeholderFields]);

  const renderEmployee = (employee) => {
    return (
      <div
        key={employee.id}
        className={employee.archived ? 'text-muted m-b-8' : 'm-b-8'}
      >
        {employee.name}
      </div>
    );
  };

  return (
    <div
      className="component-bg-white component-xl-padding-vertical component-no-horizontal-padding"
      style={{ minHeight: '85vh' }}
    >
      {/* profile left block */}
      <Col xs={3} className={styles.profileLeftBlock}>
        <div className={`${styles.stakeholderProfileAvatar} m-b-30`}>
          <img src={stakeholder.avatar} alt="profile-avatar" sizes="300x290" />
        </div>
        <div className="flex flex-align-center m-b-30">
          <div className="m-r-8">
            <StakeholderIcon />
          </div>
          <h3>{capitalize(stakeholder.name)}</h3>
        </div>
        <div className="m-b-30">
          <strong className="header-item bordered text-muted">
            <span>{capitalize(getStakeholderField('member_count').name)}</span>
            :&nbsp;
            <span className="text-primary">
              <span>
                {stakeholder[getStakeholderField('member_count').key] || 0}
              </span>
            </span>
          </strong>
        </div>
        {/* Stakeholder Analysis */}
        <div className="m-b-44">
          <div xs={12}>
            <div
              className={`${styles.componentXs} component-bg-blue component-hide-border m-b-18`}
            >
              <h4>Stakeholder Analysis</h4>
            </div>
          </div>

          <div>
            <div>{renderField(fieldInfluence.name, influenceRating)}</div>
            <div>{renderField(fieldInterest.name, interestRating)}</div>
            <div>{renderField(fieldImpact.name, impactRating, true)}</div>
          </div>

          {/* Engagement Action */}
          <div className="row">
            <div className="text-muted modal-key-label col-xs-6">
              Engagement Action
            </div>
            <div className="col-xs-6">
              {stakeholder.engagement_action || '—'}
            </div>
          </div>
        </div>
        {/* Support for Change */}
        <Row className="m-b-44">
          <Col xs={12}>
            <div
              className={`${styles.componentXs} component-bg-blue component-hide-border component-margin-bottom flex`}
            >
              <h4>{getStakeholderField('support').name}</h4>
              <Button
                aria-label="View Support for Change modal"
                type="button"
                onClick={onShowSupportForChangeModal}
                className="flex flex-align-center m-l-8 btn-last-update-icon"
              >
                <QuestionBlueIcon />
              </Button>

              <ProfileSupportForChangeModal
                show={showSupportForChangeModal}
                onHide={onHideSupportForChangeModal}
                status={stakeholder[getStakeholderField('support').key]}
                project={project}
              />
            </div>
            <div>
              <div>
                {renderSupportColors(
                  stakeholder[getStakeholderField('support').key]
                ) || '—'}
              </div>
            </div>
          </Col>
        </Row>
        {/* Engagement Frequency */}
        <Row>
          <Col xs={12}>
            <div
              className={`${styles.componentXs} component-bg-blue component-hide-border component-margin-bottom flex`}
            >
              <h4 className="">
                {getStakeholderField('engagement_frequency').name}
              </h4>
              <Button
                aria-label="View Support for Change modal"
                type="button"
                onClick={onShowEngagementFrequencyModal}
                className="flex flex-align-center m-l-8 btn-last-update-icon"
              >
                <QuestionBlueIcon />
              </Button>

              <ProfileEngagementFrequencyModal
                show={showEngagementFrequencyModal}
                onHide={onHideEngagementFrequencyModal}
                status={
                  stakeholder[getStakeholderField('engagement_frequency').key]
                }
                project={project}
              />
            </div>
            <div>
              <div>
                {renderEngageColors(
                  stakeholder[getStakeholderField('engagement_frequency').key]
                ) || '—'}
              </div>
            </div>
          </Col>
        </Row>
      </Col>

      {/* profile right block */}
      <Col xs={9} className={styles.profileRightBlock}>
        <Row>
          <Col xs={12}>
            <div
              className={`${styles.componentXs} component-bg-blue component-hide-border m-b-16`}
            >
              <h3 style={{ fontSize: '20px' }}>Stakeholder Information</h3>
            </div>

            <Row className="flex flex-wrap flex-align-start">
              {renderFields}
            </Row>
          </Col>

          <Col xs={12}>
            <div
              className={`${styles.componentXs} component-bg-blue component-hide-border component-margin-bottom`}
            >
              <h3 style={{ fontSize: '20px' }}>Participants</h3>
            </div>
          </Col>

          <Col xs={4} className="m-b-32">
            <div className="text-muted m-b-12">
              <h3 style={{ fontSize: '20px' }}>People</h3>
            </div>
            <Col>
              {groupParticipations?.length ? (
                <ParticipationsTree entities={groupParticipations} />
              ) : (
                <div>—</div>
              )}
            </Col>
          </Col>

          {/* Trusted Communicators */}
          <Col xs={4} className="m-b-32">
            <div className="text-muted m-b-12">
              <h3 style={{ fontSize: '20px' }}>Trusted Communicators</h3>
            </div>
            <div>
              {stakeholder.employees.length ? (
                stakeholder.employees.map(renderEmployee)
              ) : (
                <div>—</div>
              )}
            </div>
          </Col>
        </Row>
      </Col>
    </div>
  );
};

export default StakeholderProfile;
